import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Home as HomeIcon } from "lucide-react";

/**
 * Accessible breadcrumb. Pass an array of {label, to?} crumbs. The last
 * crumb is rendered as the current page (no link). Also emits a
 * BreadcrumbList via JSON-LD when `emitJsonLd=true`.
 *
 * Security: the JSON-LD payload is injected through `el.textContent`
 * (NOT `dangerouslySetInnerHTML`). textContent never parses HTML, so a
 * crumb label that happens to contain `</script>` or `<` cannot break
 * out of the script context.
 */
const JSONLD_ID = "breadcrumbs-jsonld";

export default function Breadcrumbs({ crumbs = [], emitJsonLd = true }) {
  const items = [{ label: "Home", to: "/", icon: HomeIcon }, ...crumbs];

  useEffect(() => {
    if (!emitJsonLd) return undefined;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const payload = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.label,
        item: c.to ? `${origin}${c.to}` : undefined,
      })),
    };
    let el = document.getElementById(JSONLD_ID);
    if (!el) {
      el = document.createElement("script");
      el.id = JSONLD_ID;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    // textContent is XSS-safe: it never interprets HTML.
    el.textContent = JSON.stringify(payload);
    return () => {
      const existing = document.getElementById(JSONLD_ID);
      if (existing) existing.remove();
    };
    // Stringify the items so identical content doesn't re-trigger the effect.
  }, [emitJsonLd, JSON.stringify(items)]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <nav aria-label="Breadcrumb" className="text-xs text-[#5F6B61]" data-testid="breadcrumbs">
      <ol className="flex items-center flex-wrap gap-1">
        {items.map((c, i) => {
          const isLast = i === items.length - 1;
          const Icon = c.icon;
          return (
            <li key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={12} strokeWidth={2} className="text-[#9AA597]" />}
              {isLast || !c.to ? (
                <span className="font-semibold text-[#3A463C]" aria-current="page">
                  {Icon ? <Icon size={12} strokeWidth={2} className="inline -mt-0.5 mr-1" /> : null}
                  {c.label}
                </span>
              ) : (
                <Link to={c.to} className="hover:text-[#5B8C66]">
                  {Icon ? <Icon size={12} strokeWidth={2} className="inline -mt-0.5 mr-1" /> : null}
                  {c.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
